import type { FC } from 'react';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { getDeviationUrl } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  PapiComment,
  PapiForum,
  PapiGallection,
  PapiUser,
  PapiModuleMyComments,
  PapiDeviation,
} from '@wix/da-papi-types';
import { useTranslation } from 'react-i18next';
import { SubjectType } from '../types';
import { MyComment } from './MyComment';
import { Url } from '@wix/da-url';
import s from './MyComment.scss';

// Helper components for reactPostprocessor - keeps tokens at single level
const StyledUserLink: FC<{ user: PapiUser }> = ({ user }) => (
  <UserLink
    className={s['userlink']}
    user={user}
    showSymbol={false}
    withTooltip
  />
);

const ContentLink: FC<{ href: string; title: string }> = ({ href, title }) => (
  <strong>
    <a href={href}>{title}</a>
  </strong>
);

const AuthorSuffix: FC<{ user?: PapiUser }> = ({ user }) =>
  user ? (
    <>
      {' by '}
      <StyledUserLink user={user} />
    </>
  ) : null;

export interface Props {
  subject: PapiModuleMyComments['results'][0]['subject'];
  comment: PapiComment;
  isOwner: boolean;
  className?: string;
}

export const MyCommentFactory: FC<Props> = ({
  subject,
  isOwner,
  comment,
  className,
}) => {
  const { t } = useTranslation();

  let title;
  let thumb: PapiDeviation | undefined;

  const commentedBy = isOwner ? (
    <span className={s['commented-by-you']}>{t('common.you')}</span>
  ) : (
    <StyledUserLink user={comment.user} />
  );

  const subjectType = Object.values(SubjectType).find(key =>
    Object.prototype.hasOwnProperty.call(subject, key)
  );

  switch (subjectType) {
    case SubjectType.DEVIATION:
    case SubjectType.COMMISSION:
      const deviationSubject = subject as { deviation: PapiDeviation };
      thumb = deviationSubject.deviation;
      if (!thumb) {
        return null;
      }
      title = thumb.status
        ? t('widgets.myComments.status', {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            commentedBy,
            contentLink: (
              <ContentLink
                href={getDeviationUrl(thumb)}
                title={t('widgets.myComments.statusLabel')}
              />
            ),
            authorSuffix: <AuthorSuffix user={thumb.author} />,
          })
        : t('widgets.myComments.deviation', {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            commentedBy,
            contentLink: (
              <ContentLink
                href={getDeviationUrl(thumb)}
                title={thumb.title ?? ''}
              />
            ),
            authorSuffix: <AuthorSuffix user={thumb.author} />,
          });
      break;

    case SubjectType.FORUM:
      const forumSubject = subject as { forum: PapiForum };
      const forum = forumSubject.forum;
      if (!forum) {
        return null;
      }
      title = t('widgets.myComments.forum', {
        postProcess: ['reactPostprocessor', 'featureBrancher'],
        commentedBy,
        forumLink: (
          <ContentLink
            href={Url.forumLink(forum.forumPath, forum.threadId)}
            title={forum.subject}
          />
        ),
        author: <StyledUserLink user={forum.author} />,
      });
      break;

    case SubjectType.COLLECTION:
      const collectionSubject = subject as { collection: PapiGallection };
      const collection = collectionSubject.collection;
      if (!collection) {
        return null;
      }
      title = t('widgets.myComments.collection', {
        postProcess: ['reactPostprocessor', 'featureBrancher'],
        commentedBy,
        ownerLink: <StyledUserLink user={collection.owner} />,
        collectionLink: (
          <ContentLink
            href={Url.userGalleryPath({
              username: collection.owner.username,
              gallectionType: collection.type as 'gallery' | 'collection',
              folderId: collection.folderId,
              folderName: collection.name,
            })}
            title={t('widgets.myComments.collectionLabel')}
          />
        ),
      });
      break;

    case SubjectType.PROFILE:
      const profileSubject = subject as { profile: PapiUser };
      const profile = profileSubject.profile;
      if (!profile) {
        return null;
      }
      title = t('widgets.myComments.profile', {
        postProcess: ['reactPostprocessor', 'featureBrancher'],
        commentedBy,
        profileLink: <StyledUserLink user={profile} />,
      });
      break;

    default:
      return null;
  }

  return (
    <MyComment
      title={title}
      thumb={thumb}
      comment={comment}
      className={className}
    />
  );
};

MyCommentFactory.displayName = 'MyCommentFactory';

export default MyCommentFactory;
